/**
 * Pelayan & pengurus (spec 2026-09-30): the vocabulary of the directory of everyone who
 * serves. Who someone is (`JENIS_PELAYAN`), the level of a place (`TINGKAT_PENUGASAN`) and
 * every position by level (`KODE_JABATAN`, in the contract) with its group on the screen,
 * whether only the admin keuskupan may assign it (spec 6), the SATU account it may carry
 * (spec 4.2) and the kinds of person who may hold it. The validators, the push rules, the
 * worker and the screens read this table; nothing else lists positions.
 *
 * Dates are the partial dates of spec 8.1, and three questions read them (the first two by
 * the controller ruling of 2026-10-01):
 *
 * - Current on a day (`aktifPada`, `berakhir`: the directory's current and ended views, the
 *   ketua's scope, accounts). A partial `sejak` starts on the first day of its period; a
 *   partial `sampai` holds through the last day of its own period ("sampai 2024" through
 *   31 December 2024, "sampai 2024-06" through 30 June), so a DPP of 2024–2027 serves all
 *   through 2027. A full-date `sampai` ends on that very day, so Selesaikan with today's
 *   date takes effect on the screen at once.
 * - Counted at the year end (`terhitungAkhirTahun`: the statistics, plan Review Focus 4).
 *   On 31 December of the year, begun by then and not ended within or before the year: a
 *   `sampai` of any precision that falls in the year means it ended during the year.
 * - Ending soon (`berakhirSegera`: the directory's row marker, spec 2026-10-03 per
 *   paroki, 11). Current on the day, with a `sampai` whose last possible day is at most 60
 *   days on.
 */
import {
  isPartialDate,
  partialDateEarliest,
  partialDateLatest,
  partialDatePrecision,
  toPartialDate,
} from './dates/partial'
import {
  type JENIS_PELAYAN,
  KODE_JABATAN,
  type Peran,
  type TINGKAT_PENUGASAN,
} from './schema/contract'
import { format } from './strings/index'
import {
  isiTingkat,
  SEBUTAN_TINGKAT_BAWAAN,
  type SebutanTingkat,
  tanpaSebutanTingkat,
} from './strings/tingkat'
import ui from './strings/ui.id.json' with { type: 'json' }
import { isiUnit, SEBUTAN_BAWAAN, type SebutanUnit, tanpaSebutanUnit } from './strings/unit'

export type JenisPelayan = (typeof JENIS_PELAYAN)[number]
export type TingkatPenugasan = (typeof TINGKAT_PENUGASAN)[number]
export type KodeJabatan = (typeof KODE_JABATAN)[number]

/**
 * The groups within a place on the screen, in this order (spec 2026-09-30, 8.2; spec 2026-10-02
 * hierarki, 5.3): Pastor dan biarawan/biarawati, Pelayan (katekis, prodiakon, Lainnya), DPP,
 * DKP, Pengurus, Staf. `grup`: a position of a parish's seksi or kelompok kategorial, which the
 * directory shows under its group, never in these lists.
 */
export const KELOMPOK_JABATAN = [
  'klerus',
  'pelayan',
  'dpp',
  'dkp',
  'pengurus',
  'staf',
  'grup',
] as const
export type KelompokJabatan = (typeof KELOMPOK_JABATAN)[number]

/** The kinds an admin paroki may add: lay people (spec 5.1, 6). */
export const JENIS_AWAM: readonly JenisPelayan[] = ['katekis', 'prodiakon', 'awam', 'lainnya']
/**
 * The kinds that may belong to an order or a congregation (spec 5.1; the deacon by the owner,
 * 2026-10-04): their person carries `ordo_kongregasi`.
 */
export const JENIS_BERORDO: readonly JenisPelayan[] = [
  'imam',
  'diakon',
  'frater',
  'bruder',
  'suster',
]
/**
 * The kinds that may carry a photo (`pelayan.foto`, owner 2026-10-03): priests (the Uskup and a
 * Mgr are `imam`), deacons and the religious. Never a lay person (`JENIS_AWAM`).
 */
export const JENIS_BERFOTO: readonly JenisPelayan[] = [
  'imam',
  'diakon',
  'frater',
  'bruder',
  'suster',
]

/** A kind that may carry a photo (`JENIS_BERFOTO`). */
export function bolehBerfoto(jenis: unknown): boolean {
  return typeof jenis === 'string' && JENIS_BERFOTO.includes(jenis as JenisPelayan)
}

/**
 * The person's row breaks the photo rule: it holds a photo while its kind may carry none. The
 * row's schema, the device's save and the push refuse such a row as stored.
 */
export function fotoTanpaHak(row: { jenis?: unknown; foto?: unknown }): boolean {
  return row.foto !== null && row.foto !== undefined && !bolehBerfoto(row.jenis)
}

export interface Jabatan {
  readonly kode: KodeJabatan
  readonly tingkat: TingkatPenugasan
  /** The other levels it holds at: a group position at its group's level (paroki or stasi). */
  readonly juga: readonly TingkatPenugasan[]
  readonly kelompok: KelompokJabatan
  /** Only the admin keuskupan may assign or change it (spec 6). */
  readonly dicadangkan: boolean
  /** The account role this position may carry (spec 4.2), or null. */
  readonly peranAkun: Peran | null
  /** The kinds of person who may hold it; null for anyone. */
  readonly jenis: readonly JenisPelayan[] | null
  /** Needs `jabatan_lain`: the name for Lainnya, the komisi or the bidang. */
  readonly perluNama: boolean
  /** Names its group (`penugasan.kelompok_id`): a leader of a parish's seksi or kelompok kategorial. */
  readonly perluKelompok: boolean
}

function j(
  kode: KodeJabatan,
  tingkat: TingkatPenugasan,
  kelompok: KelompokJabatan,
  opsi: {
    dicadangkan?: boolean
    peranAkun?: Peran
    jenis?: readonly JenisPelayan[]
    perluNama?: boolean
    juga?: readonly TingkatPenugasan[]
    perluKelompok?: boolean
  } = {},
): Jabatan {
  return {
    kode,
    tingkat,
    juga: opsi.juga ?? [],
    kelompok,
    dicadangkan: opsi.dicadangkan ?? false,
    peranAkun: opsi.peranAkun ?? null,
    jenis: opsi.jenis ?? null,
    perluNama: opsi.perluNama ?? false,
    perluKelompok: opsi.perluKelompok ?? false,
  }
}

const IMAM: readonly JenisPelayan[] = ['imam']
const KEUSKUPAN = { dicadangkan: true } as const
/** The leaders of a parish's group: at the group's level, the parish's or a stasi's. */
const GRUP = { juga: ['stasi'], perluKelompok: true } as const

const DAFTAR: readonly Jabatan[] = [
  j('uskup', 'keuskupan', 'pelayan', { ...KEUSKUPAN, jenis: IMAM }),
  j('uskup_emeritus', 'keuskupan', 'pelayan', { ...KEUSKUPAN, jenis: IMAM }),
  j('vikaris_jenderal', 'keuskupan', 'pelayan', { ...KEUSKUPAN, jenis: IMAM }),
  j('vikaris_episkopal', 'keuskupan', 'pelayan', { ...KEUSKUPAN, jenis: IMAM }),
  j('sekretaris_kanselir', 'keuskupan', 'pelayan', KEUSKUPAN),
  j('ekonom', 'keuskupan', 'pelayan', KEUSKUPAN),
  j('ketua_komisi', 'keuskupan', 'pelayan', { ...KEUSKUPAN, perluNama: true }),
  j('lainnya_keuskupan', 'keuskupan', 'pelayan', { ...KEUSKUPAN, perluNama: true }),
  j('dekan', 'dekenat', 'pelayan', { ...KEUSKUPAN, jenis: IMAM }),
  j('lainnya_dekenat', 'dekenat', 'pelayan', { ...KEUSKUPAN, perluNama: true }),
  // Release 32 (owner, 2026-11-02): both priests of the parish may get a Pastor Paroki account.
  j('pastor_kepala_paroki', 'paroki', 'klerus', {
    ...KEUSKUPAN,
    jenis: IMAM,
    peranAkun: 'pastor_paroki',
  }),
  j('pastor_rekan', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: IMAM, peranAkun: 'pastor_paroki' }),
  j('pastor_pembantu', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: IMAM }),
  j('diakon_paroki', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: ['diakon'] }),
  j('frater_top', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: ['frater'] }),
  j('bruder_paroki', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: ['bruder'] }),
  j('suster_paroki', 'paroki', 'klerus', { ...KEUSKUPAN, jenis: ['suster'] }),
  j('katekis_paroki', 'paroki', 'pelayan', { jenis: ['katekis'] }),
  j('prodiakon_paroki', 'paroki', 'pelayan', { jenis: ['prodiakon'] }),
  j('koster', 'paroki', 'staf'),
  j('staf_sekretariat', 'paroki', 'staf', { peranAkun: 'staf_paroki' }),
  j('lainnya_paroki', 'paroki', 'pelayan', { perluNama: true }),
  j('dpp_ketua', 'paroki', 'dpp'),
  j('dpp_wakil_ketua', 'paroki', 'dpp'),
  j('dpp_sekretaris', 'paroki', 'dpp'),
  j('dpp_bendahara', 'paroki', 'dpp'),
  j('dpp_koordinator_bidang', 'paroki', 'dpp', { perluNama: true }),
  j('dpp_anggota', 'paroki', 'dpp'),
  j('dkp_ketua', 'paroki', 'dkp'),
  j('dkp_sekretaris', 'paroki', 'dkp'),
  j('dkp_bendahara', 'paroki', 'dkp'),
  j('dkp_anggota', 'paroki', 'dkp'),
  j('kelompok_ketua', 'paroki', 'grup', GRUP),
  j('kelompok_wakil_ketua', 'paroki', 'grup', GRUP),
  j('kelompok_sekretaris', 'paroki', 'grup', GRUP),
  j('kelompok_bendahara', 'paroki', 'grup', GRUP),
  j('kelompok_anggota', 'paroki', 'grup', GRUP),
  j('pastor_melayani_stasi', 'stasi', 'klerus', { ...KEUSKUPAN, jenis: IMAM }),
  j('katekis_stasi', 'stasi', 'pelayan', { jenis: ['katekis'] }),
  j('prodiakon_stasi', 'stasi', 'pelayan', { jenis: ['prodiakon'] }),
  j('guru_agama', 'stasi', 'pelayan'),
  j('lainnya_stasi', 'stasi', 'pelayan', { perluNama: true }),
  j('stasi_ketua', 'stasi', 'pengurus', { peranAkun: 'ketua_stasi' }),
  j('stasi_wakil_ketua', 'stasi', 'pengurus'),
  j('stasi_sekretaris', 'stasi', 'pengurus'),
  j('stasi_bendahara', 'stasi', 'pengurus'),
  j('stasi_anggota', 'stasi', 'pengurus'),
  j('lingkungan_ketua', 'lingkungan', 'pengurus', { peranAkun: 'ketua_lingkungan' }),
  j('lingkungan_wakil_ketua', 'lingkungan', 'pengurus'),
  j('lingkungan_sekretaris', 'lingkungan', 'pengurus'),
  j('lingkungan_bendahara', 'lingkungan', 'pengurus'),
  j('lingkungan_anggota', 'lingkungan', 'pengurus'),
]

export const JABATAN: Readonly<Record<KodeJabatan, Jabatan>> = Object.fromEntries(
  DAFTAR.map((x) => [x.kode, x]),
) as Record<KodeJabatan, Jabatan>

/**
 * One current holder per place (spec 2026-09-30, 5.2; spec 2026-10-02 hierarki, 5.2): the Ketua
 * of a stasi, a lingkungan or a group (per group: several groups share a place), and the
 * parish's Pastor Kepala Paroki. The push route and the wizard count them alike.
 */
export const KETUA_TUNGGAL: readonly KodeJabatan[] = [
  'pastor_kepala_paroki',
  'stasi_ketua',
  'lingkungan_ketua',
  'kelompok_ketua',
]

/**
 * The parish positions the parish website's feed carries (spec 2026-10-03 umpan situs, 2.1;
 * GET /api/situs/v1/pelayan): the parish's clergy, then its Dewan Pastoral Paroki, each list in
 * `KODE_JABATAN`'s order.
 */
export const JABATAN_SITUS_KLERUS: readonly KodeJabatan[] = [
  'pastor_kepala_paroki',
  'pastor_rekan',
  'pastor_pembantu',
  'diakon_paroki',
  'frater_top',
]
export const JABATAN_SITUS_DPP: readonly KodeJabatan[] = [
  'dpp_ketua',
  'dpp_wakil_ketua',
  'dpp_sekretaris',
  'dpp_bendahara',
  'dpp_koordinator_bidang',
  'dpp_anggota',
]
/**
 * Every position the feed may carry: the parish's (current ones; an ended Pastor Kepala Paroki as
 * the parish's history) and the Uskup. Only these show the "Tampil di situs web" switch, and the
 * row rule refuses `tampil_di_situs` on any other (`periksaPenugasan`).
 */
export const JABATAN_SITUS: readonly KodeJabatan[] = [
  ...JABATAN_SITUS_KLERUS,
  ...JABATAN_SITUS_DPP,
  'uskup',
]

/** A position the website's feed carries (`JABATAN_SITUS`). */
export function jabatanSitus(kode: unknown): boolean {
  return typeof kode === 'string' && JABATAN_SITUS.includes(kode as KodeJabatan)
}

/**
 * The assignment's row breaks the website rule: it is marked to appear on the website
 * (`tampil_di_situs`, `true` in Postgres and an op, 1 on the device) on a position the feed does
 * not carry. The row rules (`periksaPenugasan`, so the push) and the device's save refuse it.
 */
export function situsTanpaHak(row: { jabatan?: unknown; tampil_di_situs?: unknown }): boolean {
  const v = row.tampil_di_situs
  return (v === true || v === 1 || v === 1n) && !jabatanSitus(row.jabatan)
}

/** Whether a position holds at a level: its own, or one of `juga`. */
export function berlakuDi(jab: Jabatan, tingkat: unknown): boolean {
  return tingkat === jab.tingkat || (jab.juga as readonly unknown[]).includes(tingkat)
}

/** Suggestions only: the fields take free text (spec 4.1). */
export const GELAR_SARAN: readonly string[] = ['Mgr.', 'RP', 'RD', 'Diakon', 'Fr.', 'Br.', 'Sr.']
/** A priest's, a deacon's or a frater's: diocesan, or the orders that serve here. */
export const ORDO_SARAN: readonly string[] = ['diosesan', 'OSC', 'MSC', 'OFM', 'SVD', 'CSsR']
export const KONGREGASI_SARAN: readonly string[] = ['CB', 'FSGM', 'JMJ', 'PRR', 'SSpS']
/** A brother's: the brothers' congregations first, then the orders (a brother is never diocesan). */
export const KONGREGASI_BRUDER_SARAN: readonly string[] = [
  'FIC',
  'BHK',
  'CSA',
  'MTB',
  ...ORDO_SARAN.filter((o) => o !== 'diosesan'),
]

/** The order or congregation field of a kind: its label, its hint, its suggestions. */
export interface IsianOrdo {
  label: string
  petunjuk: string | null
  saran: readonly string[]
}

/**
 * The order or congregation field a kind shows (owner, 2026-10-04), or null for a kind that has
 * none (`JENIS_BERORDO`): Ordo for a priest, with the "diosesan" hint; Ordo / kongregasi for a
 * deacon, a frater and a brother; Kongregasi for a sister.
 */
export function isianOrdo(jenis: JenisPelayan | ''): IsianOrdo | null {
  const w = ui.pelayan.wizard
  switch (jenis) {
    case 'imam':
      return { label: w.ordo, petunjuk: w.ordoPetunjuk, saran: ORDO_SARAN }
    case 'diakon':
    case 'frater':
      return { label: w.ordoKongregasi, petunjuk: null, saran: ORDO_SARAN }
    case 'bruder':
      return { label: w.ordoKongregasi, petunjuk: null, saran: KONGREGASI_BRUDER_SARAN }
    case 'suster':
      return { label: w.kongregasi, petunjuk: null, saran: KONGREGASI_SARAN }
    default:
      return null
  }
}

export function jabatanDari(kode: unknown): Jabatan | null {
  if (typeof kode !== 'string' || !(KODE_JABATAN as readonly string[]).includes(kode)) return null
  return JABATAN[kode as KodeJabatan]
}

export function adalahDicadangkan(kode: unknown): boolean {
  return jabatanDari(kode)?.dicadangkan ?? false
}

export function peranAkunJabatan(kode: unknown): Peran | null {
  return jabatanDari(kode)?.peranAkun ?? null
}

/** The Ketua position of a stasi or lingkungan ("Ketua belum ada" offers it). */
export function ketuaTempat(tingkat: TingkatPenugasan): KodeJabatan | null {
  if (tingkat === 'stasi') return 'stasi_ketua'
  if (tingkat === 'lingkungan') return 'lingkungan_ketua'
  return null
}

/**
 * The positions a place and a person offer, as far as the actor may assign them (spec 8.3):
 * the level's own positions first (at a parish the Pastor Kepala Paroki leads), then those that
 * also hold there (a stasi's group leaders).
 */
export function jabatanPilihan(o: {
  tingkat: TingkatPenugasan
  jenis: JenisPelayan
  peran: Peran
}): KodeJabatan[] {
  const boleh = (x: Jabatan) =>
    (x.jenis === null || x.jenis.includes(o.jenis)) &&
    (o.peran === 'admin_keuskupan' || !x.dicadangkan)
  return [
    ...DAFTAR.filter((x) => x.tingkat === o.tingkat && boleh(x)),
    ...DAFTAR.filter((x) => x.tingkat !== o.tingkat && x.juga.includes(o.tingkat) && boleh(x)),
  ].map((x) => x.kode)
}

const LABEL_JENIS: Record<string, string> = ui.pelayan.jenis
const LABEL_JABATAN: Record<string, string> = ui.pelayan.jabatan
const LABEL_BERNAMA: Record<string, string> = ui.pelayan.jabatanBernama

/** A table's own entry only: "constructor" or "toString" never read as a label. */
function milik(tabel: Record<string, string>, kunci: string): string | undefined {
  return Object.hasOwn(tabel, kunci) ? tabel[kunci] : undefined
}

export function labelJenis(jenis: string): string {
  return milik(LABEL_JENIS, jenis) ?? jenis
}

/**
 * "Ketua Lingkungan"; "Ketua Komisi Kateketik"; a Lainnya reads its own name; a group position
 * reads its group ("Ketua / Koordinator OMK Stasi St. Yosef"). A lingkungan's positions say the
 * parish's word for its smallest unit (`sebutan`, Sebutan unit terkecil: "Ketua Kombas"); the
 * middle level's say the diocese's word (`tingkat`, release 16: "Ketua Wilayah", "Lainnya
 * (wilayah)"; "Dekan" where the diocese says Dekenat).
 */
export function labelJabatan(
  kode: string,
  lain?: string | null,
  kelompok?: string | null,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
  tingkat: SebutanTingkat = SEBUTAN_TINGKAT_BAWAAN,
): string {
  const nama = (lain ?? '').trim()
  const jab = jabatanDari(kode)
  if (jab === null) return kode
  const label = (k: string) => {
    const l = milik(LABEL_JABATAN, k)
    return l === undefined ? undefined : isiTingkat(isiUnit(l, sebutan), tingkat)
  }
  const grup = (kelompok ?? '').trim()
  if (jab.perluKelompok && grup !== '') {
    return format(ui.pelayan.jabatanKelompok, { jabatan: label(kode) ?? kode, kelompok: grup })
  }
  if (nama !== '' && jab.perluNama) {
    const bernama = milik(LABEL_BERNAMA, kode)
    return bernama === undefined ? nama : format(bernama, { nama })
  }
  return label(kode) ?? kode
}

/** "RP Yohanes K., OSC": title, name, the order after a comma (never "diosesan"). */
export function namaTampil(p: {
  nama: string
  gelar?: string | null | undefined
  ordo_kongregasi?: string | null | undefined
}): string {
  const gelar = (p.gelar ?? '').trim()
  const nama = p.nama.trim()
  const ordo = (p.ordo_kongregasi ?? '').trim()
  const depan = gelar === '' ? nama : `${gelar} ${nama}`
  return ordo === '' || ordo.toLowerCase() === 'diosesan' ? depan : `${depan}, ${ordo}`
}

/** A name as it is stored (uji D14): no edge spaces, single spaces inside. */
export function rapikanNamaPelayan(nama: string): string {
  return nama.trim().replace(/\s+/g, ' ')
}

/**
 * For the "same person already listed" check: lower case, single spaces, one form of each
 * accented letter, and the curly apostrophe a phone types read as the straight one (uji D13:
 * "O’Neil" is "O'Neil").
 */
export function normalNama(nama: string): string {
  return rapikanNamaPelayan(nama.normalize('NFC'))
    .replace(/[\u2018\u2019\u02bc`\u00b4]/g, "'")
    .toLowerCase()
}

/** The date when it is a readable partial date; anything else reads as not given. */
function sah(tanggal: unknown): string | null {
  return typeof tanggal === 'string' && isPartialDate(tanggal) ? tanggal : null
}

function awal(tanggal: unknown): string | null {
  const t = sah(tanggal)
  return t === null ? null : partialDateEarliest(t)
}

/**
 * Ended by `tanggal` (a full `YYYY-MM-DD`). A full-date `sampai` ends on its own day; a
 * partial one after the last day of its month or year. An empty or unreadable `sampai` has
 * not ended.
 */
export function berakhir(t: { sampai: string | null }, tanggal: string): boolean {
  const s = sah(t.sampai)
  if (s === null) return false
  return partialDatePrecision(s) === 'hari' ? s <= tanggal : partialDateLatest(s) < tanggal
}

/** Current on `tanggal` (a full `YYYY-MM-DD`): started by then and not ended by it. */
export function aktifPada(
  t: { sejak: string | null; sampai: string | null },
  tanggal: string,
): boolean {
  const mulai = awal(t.sejak)
  if (mulai !== null && mulai > tanggal) return false
  return !berakhir(t, tanggal)
}

/** How many days ahead an end counts as ending soon (spec 2026-10-03 per paroki, 11). */
const HARI_BERAKHIR_SEGERA = 60

/**
 * Ending soon on `tanggal` (a full `YYYY-MM-DD`): current that day (`aktifPada`), with a
 * `sampai` whose last possible day ("2026" is 31 December, "2026-11" 30 November) falls at
 * most `HARI_BERAKHIR_SEGERA` days on; the 60th day counts, the 61st does not. A term already
 * over is ended (`berakhir`), not ending; one without a readable `sampai` never ends.
 */
export function berakhirSegera(
  t: { sejak: string | null; sampai: string | null },
  tanggal: string,
): boolean {
  const s = sah(t.sampai)
  if (s === null || !aktifPada(t, tanggal)) return false
  const batas = toPartialDate(
    new Date(Date.parse(`${tanggal}T00:00:00Z`) + HARI_BERAKHIR_SEGERA * 86_400_000),
  )
  return partialDateLatest(s) <= batas
}

/**
 * What a term offers for a SATU account, the one rule of the tree's "Buat akun" (spec
 * 2026-10-03 per paroki, 10) and the person's entry (spec 2026-09-30, 5.3), by the
 * API's own refusals (`pastikanBisaDiberiAkun`): a position that carries an account, without
 * any `sampai` (setting one switches an account off) and without an account (`pengguna_id`, as
 * the server reads it, whether or not the account's row is on the device), which the session
 * may change. Begun by `hariIni` it is `buat`; a term that begins later waits for its first
 * day, `nanti`. Null otherwise.
 */
export function tawaranAkun(
  t: {
    jabatan: string
    sejak: string | null
    sampai: string | null
    penggunaId: string | null
    bolehUbah: boolean
  },
  hariIni: string,
): 'buat' | 'nanti' | null {
  if (!t.bolehUbah || t.penggunaId !== null || t.sampai !== null) return null
  if (peranAkunJabatan(t.jabatan) === null) return null
  return aktifPada(t, hariIni) ? 'buat' : 'nanti'
}

/**
 * Current together on some day from `hariIni` on, each by `aktifPada`: how one Ketua per
 * place is counted (spec 5.2), by the push route and by the wizard before it. Each term is
 * one run of days, so if they share any such day they share the latest of `hariIni` and
 * both starts. A term against itself: in force today or on a later day.
 */
export function masaBersamaan(
  a: { sejak: string | null; sampai: string | null },
  b: { sejak: string | null; sampai: string | null },
  hariIni: string,
): boolean {
  let hari = hariIni
  for (const mulai of [awal(a.sejak), awal(b.sejak)]) {
    if (mulai !== null && mulai > hari) hari = mulai
  }
  return aktifPada(a, hari) && aktifPada(b, hari)
}

/**
 * Counted in the statistics of `tahun` (plan Review Focus 4): begun by 31 December and not
 * ended within or before the year. "Sampai 2026" or "sampai 2026-11" ended during 2026 and
 * is not counted for it, though it is current on the day until its period is over;
 * "sejak 2026-12" is counted.
 */
export function terhitungAkhirTahun(
  t: { sejak: string | null; sampai: string | null },
  tahun: number,
): boolean {
  const akhirTahun = `${String(tahun).padStart(4, '0')}-12-31`
  const mulai = awal(t.sejak)
  if (mulai !== null && mulai > akhirTahun) return false
  const selesai = awal(t.sampai)
  return selesai === null || selesai > akhirTahun
}

/** "sejak 2021", "2024–2027", "2024" (both ends in one year), "sampai 2027", or nothing. */
export function teksMasa(sejak: string | null, sampai: string | null): string {
  const tahun = (d: string | null) => sah(d)?.slice(0, 4) ?? null
  const dari = tahun(sejak)
  const hingga = tahun(sampai)
  if (dari !== null && hingga !== null) {
    return dari === hingga ? dari : format(ui.pelayan.masa.rentang, { dari, sampai: hingga })
  }
  if (dari !== null) return format(ui.pelayan.masa.sejak, { tahun: dari })
  if (hingga !== null) return format(ui.pelayan.masa.sampai, { tahun: hingga })
  return ''
}

/** "Lingkungan St. Maria" → "St. Maria"; "Stasi Syuru" → "Syuru"; "Paroki Katedral" → "Katedral". */
export function tanpaAwalanTempat(nama: string): string {
  return nama.trim().replace(/^(lingkungan|stasi|paroki)\s+/i, '')
}

/**
 * A parish's name where the words around it already say "Paroki" (a sacrament received at
 * another parish on the family card, the Kartu Keluarga's note "… di Paroki {paroki}", a sheet's
 * label; controller ruling 2026-10-03): one leading "Paroki" dropped, in any case, so it never
 * reads "Paroki Paroki St. …". "Paroki St. Petrus Sawa Erma" → "St. Petrus Sawa Erma"; a name
 * typed without it stays as typed, trimmed.
 */
export function tanpaKataParoki(nama: string): string {
  return nama.trim().replace(/^paroki\s+/i, '')
}

export interface TugasSingkat {
  jabatan: string
  jabatan_lain: string | null
  tingkat: string
  /** The place's name as stored; null for a diocese position. */
  tempat: string | null
  sejak: string | null
  sampai: string | null
  /** The group's name, for a group position. */
  kelompok?: string | null | undefined
}

/**
 * The one line the person record shows (spec 8.5): "Ketua Lingkungan St. Maria sejak 2025". A
 * parish-level position leaves the parish out (the record is of that parish); a group position
 * names its group instead of the place ("Ketua / Koordinator OMK Stasi St. Yosef sejak 2025"),
 * or the place while its group's name is not on the device yet. A middle-level position whose
 * own words say the level (its head, an unnamed Lainnya) says it once: "Ketua Wilayah Agats
 * sejak 2021", never "Ketua Wilayah Wilayah Agats" (release 16); a named one ("Sekretaris")
 * reads the place as typed: "Sekretaris Wilayah Agats sejak 2021" (release 16 review M1).
 */
export function teksTugasSingkat(
  t: TugasSingkat,
  sebutan: SebutanUnit = SEBUTAN_BAWAAN,
  tingkat: SebutanTingkat = SEBUTAN_TINGKAT_BAWAAN,
): string {
  const grup = jabatanDari(t.jabatan)?.perluKelompok === true && (t.kelompok ?? '').trim() !== ''
  const sebutTingkat =
    t.jabatan === 'dekan' ||
    (t.jabatan === 'lainnya_dekenat' && (t.jabatan_lain ?? '').trim() === '')
  const tempat =
    grup || t.tempat === null || t.tingkat === 'paroki' || t.tingkat === 'keuskupan'
      ? ''
      : t.tingkat === 'dekenat'
        ? sebutTingkat
          ? tanpaSebutanTingkat(t.tempat.trim(), tingkat)
          : t.tempat.trim()
        : tanpaSebutanUnit(tanpaAwalanTempat(t.tempat), sebutan)
  const masa = teksMasa(t.sejak, t.sampai)
  return [labelJabatan(t.jabatan, t.jabatan_lain, t.kelompok, sebutan, tingkat), tempat, masa]
    .filter((s) => s !== '')
    .join(' ')
}

// ---------------------------------------------------------------------------
// The row rules of an assignment (spec 5.2), shared by Zod, the push rule and the wizard
// ---------------------------------------------------------------------------

export type KodeMasalahPenugasan = keyof typeof ui.pelayan.validasi

export interface MasalahPenugasan {
  kolom:
    | 'jabatan'
    | 'tingkat'
    | 'unit_id'
    | 'paroki_id'
    | 'pengguna_id'
    | 'jabatan_lain'
    | 'sampai'
    | 'kelompok_id'
    | 'tampil_di_situs'
  kode: Exclude<KodeMasalahPenugasan, 'ketuaSudahAda'>
  pesan: string
}

export interface PenugasanDiperiksa {
  paroki_id?: unknown
  unit_id?: unknown
  tingkat?: unknown
  jabatan?: unknown
  jabatan_lain?: unknown
  sejak?: unknown
  sampai?: unknown
  pengguna_id?: unknown
  kelompok_id?: unknown
  tampil_di_situs?: unknown
}

const teksAtauNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)

/**
 * The position is known and belongs to the level; a diocese position has no place and no
 * parish, a dekenat position no parish, every other one a place of its parish (a parish-
 * level position is at the parish itself); an account only where the position allows one;
 * a named position has its name; a group position names its group and no other position does
 * (spec 2026-10-02 hierarki, 5.2); a position that also holds at another level is judged at the
 * level the row names; only a position the website's feed carries may be marked to appear on it
 * (`tampil_di_situs`, `JABATAN_SITUS`; spec 2026-10-03 umpan situs, 1); `sampai` is not before
 * `sejak` at their shared precision
 * (the day rule then makes the row current on `sejak`'s first day, unless `sampai` is that
 * very day as a full date: begun and ended the same day). Whether the place really
 * is of that level is checked where the tree is at hand (the push route and the wizard).
 */
export function periksaPenugasan(t: PenugasanDiperiksa): MasalahPenugasan[] {
  const pesan = ui.pelayan.validasi
  const out: MasalahPenugasan[] = []
  const tambah = (kolom: MasalahPenugasan['kolom'], kode: MasalahPenugasan['kode']) =>
    out.push({ kolom, kode, pesan: pesan[kode] })
  const jab = jabatanDari(t.jabatan)
  if (jab === null) {
    tambah('jabatan', 'jabatanTidakDikenal')
    return out
  }
  if (!berlakuDi(jab, t.tingkat)) tambah('tingkat', 'tingkatTidakCocok')
  // The place rules read the row's own level when the position holds there (a stasi's group).
  const tingkat: TingkatPenugasan = berlakuDi(jab, t.tingkat)
    ? (t.tingkat as TingkatPenugasan)
    : jab.tingkat
  const unit = teksAtauNull(t.unit_id)
  const paroki = teksAtauNull(t.paroki_id)
  if (tingkat === 'keuskupan') {
    if (unit !== null) tambah('unit_id', 'tempatKeuskupan')
  } else if (unit === null) {
    tambah('unit_id', 'tempatWajib')
  }
  if (tingkat === 'keuskupan' || tingkat === 'dekenat') {
    if (paroki !== null) tambah('paroki_id', 'parokiTidakCocok')
  } else if (paroki === null || (tingkat === 'paroki' && unit !== null && unit !== paroki)) {
    tambah('paroki_id', 'parokiTidakCocok')
  }
  if (teksAtauNull(t.pengguna_id) !== null && jab.peranAkun === null) {
    tambah('pengguna_id', 'akunTidakBoleh')
  }
  if (jab.perluNama && (teksAtauNull(t.jabatan_lain) ?? '').trim() === '') {
    tambah('jabatan_lain', 'namaJabatanWajib')
  }
  const kelompok = teksAtauNull(t.kelompok_id)
  if (jab.perluKelompok && kelompok === null) tambah('kelompok_id', 'kelompokWajib')
  if (!jab.perluKelompok && kelompok !== null) tambah('kelompok_id', 'kelompokTidakBoleh')
  if (situsTanpaHak(t)) tambah('tampil_di_situs', 'situsTidakBoleh')
  // Compared at the coarser of the two precisions: "sejak 2024-05-01, sampai 2024" is one
  // year (current from 1 May to 31 December); "sejak 2024-05, sampai 2024-03" is refused.
  const mulai = sah(t.sejak)
  const selesai = sah(t.sampai)
  if (mulai !== null && selesai !== null) {
    const n = Math.min(mulai.length, selesai.length)
    if (selesai.slice(0, n) < mulai.slice(0, n)) tambah('sampai', 'sampaiSebelumSejak')
  }
  return out
}
